import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { PLATFORM_LABEL, severityRank, type Finding, type Severity } from '../types';
import { FindingSummary } from './findings/FindingSummary';
import { LlmVerdictDisplay } from './findings/LlmVerdictDisplay';
import { findEvidenceRenderer } from './findings/registry';
import { PlatformIcon } from './PlatformIcon';
import { CollapsedTriagePill, FindingTriageSection } from './FindingTriageSection';

interface FindingCardProps {
  finding: Finding;
  cidName: string;
  auditId: string;
  // True when this card is the deep-link target (e.g. arrived from the "What changed" diff view).
  // Such a card auto-expands, scrolls into view, and briefly highlights.
  focused?: boolean;
}

export const FindingCard = ({ finding, cidName, auditId, focused = false }: FindingCardProps) => {
  const isUnavailable = finding.status === 'unavailable';
  const Renderer = findEvidenceRenderer(finding.checkId, finding.platform, finding.isJudgmentRow);

  // Default to COLLAPSED for every card so the page reads as a bird's-eye view of the whole
  // audit. The interpretive content (LLM summary for deterministic checks, verdict block for
  // judgment rows) is rendered above the collapse boundary so it stays visible at scan
  // time — the auditor expands a card only when they want the supporting data table or to
  // triage. User can manually toggle (override = boolean); changing triage resets the override
  // so the default behavior takes over again.
  const defaultCollapsed = true;
  // A focused (deep-linked) card starts expanded so the user lands on the detail they clicked for.
  const [override, setOverride] = useState<boolean | null>(focused ? false : null);
  const collapsed = override ?? defaultCollapsed;
  // Reset the manual override when triage ACTUALLY changes. Compare against the previous value
  // (rather than a "skip first mount" flag) so this is StrictMode-safe — a double-invoked mount
  // effect sees the same triage and won't clobber a focused card's start-expanded state.
  const prevTriageRef = useRef(finding.triage);
  useEffect(() => {
    if (prevTriageRef.current !== finding.triage) {
      prevTriageRef.current = finding.triage;
      setOverride(null);
    }
  }, [finding.triage]);

  const toggle = () => setOverride(prev => (prev === null ? !defaultCollapsed : !prev));

  // When a card transitions from expanded → collapsed, scroll its header to the top of the
  // scroll container. Without this, the cards below shift up into where the user's eye was
  // (around the triage pills), leaving them visually "landed" mid-content of the next card.
  // Aligning the just-collapsed card to the top puts the next account immediately beneath.
  const articleRef = useRef<HTMLElement>(null);
  const wasCollapsedRef = useRef(collapsed);
  useEffect(() => {
    if (collapsed && !wasCollapsedRef.current) {
      articleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    wasCollapsedRef.current = collapsed;
  }, [collapsed]);

  // Deep-link focus: scroll the card into view and pulse a highlight, once, on arrival.
  const [focusHighlight, setFocusHighlight] = useState(false);
  useEffect(() => {
    if (!focused) return undefined;
    setFocusHighlight(true);
    // Align the card's TOP to the top of the scroll container (block: 'start') — block: 'center'
    // leaves the header of a tall card above the fold. Matches the collapse-scroll behavior above.
    articleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => setFocusHighlight(false), 2400);
    return () => clearTimeout(t);
  }, [focused]);

  const triaged = finding.triage !== 'untriaged';
  const hasNotePeek = collapsed && triaged && finding.auditorNote.trim().length > 0;

  // Interpretive content shows above the collapse boundary so it's visible at scan even on
  // collapsed cards. Three flavors:
  //   - summary (deterministic checks with template/LLM summary) → FindingSummary
  //   - llmVerdict (judgment rows) → LlmVerdictDisplay
  //   - unavailable findings → statusReason rendered in a neutral band so the auditor knows
  //     why the check didn't apply without expanding
  // We hide it when the card is collapsed AND triaged — the auditor has already digested it;
  // the note-peek shows their own decision rationale instead.
  const hasInterpretive = isUnavailable
    ? Boolean(finding.statusReason)
    : Boolean(finding.summary || finding.llmVerdict);
  const showInterpretive = hasInterpretive && (!collapsed || !triaged);

  const diffModifier = getDiffModifier(finding);

  return (
    <article
      ref={articleRef}
      className={classNames('finding-card', {
        'finding-card--collapsed': collapsed,
        'finding-card--unavailable': isUnavailable,
        'finding-card--triaged': finding.triage !== 'untriaged',
        'finding-card--focused': focusHighlight,
        [`finding-card--${diffModifier}`]: diffModifier !== null
      })}
    >
      <header
        className='finding-card__header'
        role='button'
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className='finding-card__title-block'>
          <div className='finding-card__top-row'>
            <div className='finding-card__chips'>
              <span className={`platform-chip platform-chip--${finding.platform}`}>
                <PlatformIcon platform={finding.platform} size={12} />
                {PLATFORM_LABEL[finding.platform]}
              </span>
              <h4 className='finding-card__account'>{cidName}</h4>
              {finding.isJudgmentRow && (
                <span
                  className='finding-card__ai-badge'
                  title='Severity and verdict drafted by Claude; auditor reviews and triages'
                >
                  AI-drafted
                </span>
              )}
            </div>
            <div className='finding-card__badges'>
              <DiffBadge finding={finding} />
              <CollapsedTriagePill triage={finding.triage} />
              <SeverityBadge severity={finding.severity} unavailable={isUnavailable} />
            </div>
          </div>
          {hasNotePeek && <div className='finding-card__note-peek'>{finding.auditorNote}</div>}
        </div>
        <span
          className={classNames('finding-card__chevron', {
            'finding-card__chevron--collapsed': collapsed
          })}
          aria-hidden
        >
          ▾
        </span>
      </header>

      {showInterpretive && (
        <div className='finding-card__interpretive'>
          {isUnavailable ? (
            <p className='finding-card__unavailable-reason'>
              <span className='finding-card__unavailable-tag'>Data unavailable</span>{' '}
              {finding.statusReason}
            </p>
          ) : finding.summary ? (
            <FindingSummary summary={finding.summary} />
          ) : finding.llmVerdict ? (
            <LlmVerdictDisplay verdict={finding.llmVerdict} />
          ) : null}
        </div>
      )}

      {!collapsed && (
        <div className='finding-card__body'>
          {!isUnavailable && (
            // Renderer is a stable component looked up from the evidence registry's module-level
            // map (see line ~22) — not a component created during render — so this rule is a
            // false positive here.
            // eslint-disable-next-line react-hooks/static-components
            <Renderer
              evidence={finding.evidence}
              llmVerdict={finding.llmVerdict}
              summary={finding.summary}
              isJudgmentRow={finding.isJudgmentRow}
            />
          )}
          <FindingTriageSection
            auditId={auditId}
            findingId={finding.id}
            triage={finding.triage}
            auditorNote={finding.auditorNote}
          />
        </div>
      )}
    </article>
  );
};

const SEVERITY_LABEL: Record<Severity, string> = {
  large: 'Large opportunity',
  medium: 'Medium opportunity',
  small: 'Small opportunity',
  no_issue: 'No issues',
  na: 'N/A'
};

const SeverityBadge = ({ severity, unavailable }: { severity: Severity; unavailable: boolean }) => {
  if (unavailable) {
    return <span className='severity-badge severity-badge--unavailable'>Unavailable</span>;
  }
  return (
    <span className={`severity-badge severity-badge--${severity}`}>{SEVERITY_LABEL[severity]}</span>
  );
};

const SEVERITY_SHORT: Record<Severity, string> = {
  large: 'large',
  medium: 'medium',
  small: 'small',
  no_issue: 'clean',
  na: 'n/a'
};

// Per-finding delta badge. Conveys at a glance whether this finding is NEW since the previous
// audit, whether its severity escalated / de-escalated, or whether only the auditor's triage
// moved. Renders nothing for unchanged findings or when there's no prior audit to compare.
const DiffBadge = ({ finding }: { finding: Finding }) => {
  const { diff } = finding;
  if (!diff || diff.status === 'unchanged') return null;

  if (diff.status === 'new') {
    return (
      <span
        className='diff-badge diff-badge--new'
        title='This finding did not exist in the previous audit.'
      >
        NEW
      </span>
    );
  }

  // status === 'changed'
  const severityMoved =
    diff.previousSeverity !== undefined && diff.previousSeverity !== finding.severity;
  const statusFlipped = diff.previousStatus !== finding.status;

  if (severityMoved) {
    const direction = compareSeverity(finding.severity, diff.previousSeverity!);
    const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
    const tone =
      direction === 'up'
        ? 'diff-badge--worse'
        : direction === 'down'
          ? 'diff-badge--better'
          : 'diff-badge--lateral';
    const prevLabel = SEVERITY_SHORT[diff.previousSeverity!];
    const nowLabel = SEVERITY_SHORT[finding.severity];
    return (
      <span
        className={`diff-badge ${tone}`}
        title={`Severity moved from ${prevLabel} to ${nowLabel} since the previous audit.`}
      >
        {arrow} from {prevLabel}
      </span>
    );
  }

  if (statusFlipped) {
    const label = finding.status === 'unavailable' ? 'now unavailable' : 'now available';
    return (
      <span
        className='diff-badge diff-badge--lateral'
        title='Availability changed since the previous audit.'
      >
        {label}
      </span>
    );
  }

  // Triage-only change: less interesting but still worth indicating subtly.
  return (
    <span className='diff-badge diff-badge--triage' title='Triage moved since the previous audit.'>
      triage updated
    </span>
  );
};

function compareSeverity(current: Severity, previous: Severity): 'up' | 'down' | 'flat' {
  const c = severityRank(current);
  const p = severityRank(previous);
  if (c > p) return 'up';
  if (c < p) return 'down';
  return 'flat';
}

// Card-level diff modifier — paints a left-edge stripe so changed cards are pickup-able at
// scan speed without having to read the per-card badge. Returns null when nothing to surface.
function getDiffModifier(
  finding: Finding
): 'diff-new' | 'diff-worse' | 'diff-better' | 'diff-lateral' | null {
  const { diff } = finding;
  if (!diff || diff.status === 'unchanged') return null;
  if (diff.status === 'new') return 'diff-new';
  // status === 'changed' — direction comes from severity if it moved; otherwise lateral.
  if (diff.previousSeverity !== undefined && diff.previousSeverity !== finding.severity) {
    const dir = compareSeverity(finding.severity, diff.previousSeverity);
    if (dir === 'up') return 'diff-worse';
    if (dir === 'down') return 'diff-better';
  }
  return 'diff-lateral';
}
